import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  X, Download, FileText, FileImage, FileVideo, 
  File, Loader2, ExternalLink, AlertCircle 
} from 'lucide-react';
import { Button } from './ui/button';
import { adjuntosAPI } from '../api';
import { toast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tipos de archivo que se pueden previsualizar directamente
const PREVIEWABLE_IMAGES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp'];
const PREVIEWABLE_VIDEOS = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'];
const PREVIEWABLE_AUDIO = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp3'];
const PREVIEWABLE_PDF = ['application/pdf'];
const OFFICE_TYPES = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel', 
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation'
];
const TEXT_TYPES = ['text/plain', 'text/html', 'text/css', 'text/javascript', 'application/json', 'application/xml'];

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const FilePreview = () => {
  const { adjuntoId } = useParams();
  const navigate = useNavigate();
  const [fileInfo, setFileInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [textContent, setTextContent] = useState(null);

  const token = localStorage.getItem('lexduo_token');
  const previewUrl = `${API_URL}/api/adjuntos/preview/${adjuntoId}`;
  const previewUrlWithAuth = token ? `${previewUrl}?token=${token}` : previewUrl;

  useEffect(() => {
    const loadFileInfo = async () => {
      try {
        const response = await adjuntosAPI.getInfo(adjuntoId);
        setFileInfo(response.data);
        
        // Si es archivo de texto, cargar contenido
        if (TEXT_TYPES.includes(response.data.tipo_archivo)) {
          const textResponse = await fetch(previewUrl, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const text = await textResponse.text();
          setTextContent(text);
        }
      } catch (err) {
        setError('No se pudo cargar la información del archivo');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    if (!token) {
      setError('No se encontró token de autenticación');
      setLoading(false);
      return;
    }
    
    loadFileInfo();
  }, [adjuntoId, previewUrl, token]);

  const handleDownload = async () => {
    try {
      const response = await adjuntosAPI.download(adjuntoId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileInfo?.nombre || 'archivo');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudo descargar el archivo",
        variant: "destructive"
      });
    }
  };

  const handleClose = () => {
    window.close();
    // Si no se puede cerrar (por ejemplo, si es la única pestaña), navegar atrás
    setTimeout(() => navigate(-1), 100);
  };

  const renderPreview = () => {
    if (!fileInfo) return null;
    
    const mimeType = fileInfo.tipo_archivo || '';
    
    // Imágenes
    if (PREVIEWABLE_IMAGES.includes(mimeType)) {
      return (
        <div className="flex items-center justify-center h-full bg-gray-900 p-4">
          <img 
            src={previewUrlWithAuth}
            alt={fileInfo.nombre}
            className="max-w-full max-h-full object-contain"
            style={{ maxHeight: 'calc(100vh - 120px)' }}
            onError={() => setError('No se pudo cargar la imagen')}
          />
        </div>
      );
    }
    
    // Videos
    if (PREVIEWABLE_VIDEOS.includes(mimeType)) {
      return (
        <div className="flex items-center justify-center h-full bg-gray-900 p-4">
          <video 
            controls 
            className="max-w-full max-h-full"
            style={{ maxHeight: 'calc(100vh - 120px)' }}
          >
            <source src={previewUrlWithAuth} type={mimeType} />
            Tu navegador no soporta la reproducción de video.
          </video>
        </div>
      );
    }
    
    // Audio
    if (PREVIEWABLE_AUDIO.includes(mimeType)) {
      return (
        <div className="flex flex-col items-center justify-center h-full bg-gray-100 p-8">
          <File className="w-24 h-24 text-blue-500 mb-6" />
          <p className="text-lg font-medium mb-4">{fileInfo.nombre}</p>
          <audio controls className="w-full max-w-md">
            <source src={previewUrlWithAuth} type={mimeType} />
            Tu navegador no soporta la reproducción de audio.
          </audio>
        </div>
      );
    }
    
    // PDF
    if (PREVIEWABLE_PDF.includes(mimeType)) {
      return (
        <iframe
          src={previewUrlWithAuth}
          title={fileInfo.nombre}
          className="w-full h-full border-0"
          style={{ minHeight: 'calc(100vh - 80px)' }}
        />
      );
    }
    
    // Archivos de Office - usar Google Docs Viewer
    if (OFFICE_TYPES.includes(mimeType)) {
      // Google Docs Viewer necesita una URL pública, así que mostramos mensaje alternativo
      return (
        <div className="flex flex-col items-center justify-center h-full bg-gray-100 p-8">
          <FileText className="w-24 h-24 text-blue-600 mb-6" />
          <h2 className="text-xl font-semibold mb-2">{fileInfo.nombre}</h2>
          <p className="text-gray-600 mb-6 text-center max-w-md">
            Los documentos de Office no se pueden previsualizar directamente en el navegador.
            Por favor, descarga el archivo para verlo.
          </p>
          <Button onClick={handleDownload} className="bg-blue-600 hover:bg-blue-700">
            <Download className="w-4 h-4 mr-2" />
            Descargar para ver
          </Button>
        </div>
      );
    }
    
    // Archivos de texto
    if (TEXT_TYPES.includes(mimeType) && textContent !== null) {
      return (
        <div className="h-full bg-gray-900 p-4 overflow-auto">
          <pre className="text-green-400 font-mono text-sm whitespace-pre-wrap">
            {textContent}
          </pre>
        </div>
      );
    }
    
    // Tipo no soportado
    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-100 p-8">
        <File className="w-24 h-24 text-gray-400 mb-6" />
        <h2 className="text-xl font-semibold mb-2">{fileInfo.nombre}</h2>
        <p className="text-gray-600 mb-2">Tipo: {mimeType || 'Desconocido'}</p>
        <p className="text-gray-600 mb-6">Tamaño: {formatFileSize(fileInfo.tamano)}</p>
        <p className="text-gray-500 mb-6 text-center max-w-md">
          Este tipo de archivo no se puede previsualizar. 
          Descárgalo para abrirlo con la aplicación correspondiente.
        </p>
        <Button onClick={handleDownload} className="bg-blue-600 hover:bg-blue-700">
          <Download className="w-4 h-4 mr-2" />
          Descargar archivo
        </Button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Cargando archivo...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={handleClose} variant="outline">
            Cerrar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-white flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 text-white">
        <div className="flex items-center gap-3">
          {PREVIEWABLE_IMAGES.includes(fileInfo?.tipo_archivo) && <FileImage className="w-5 h-5" />}
          {PREVIEWABLE_VIDEOS.includes(fileInfo?.tipo_archivo) && <FileVideo className="w-5 h-5" />}
          {PREVIEWABLE_PDF.includes(fileInfo?.tipo_archivo) && <FileText className="w-5 h-5" />}
          {!PREVIEWABLE_IMAGES.includes(fileInfo?.tipo_archivo) && 
           !PREVIEWABLE_VIDEOS.includes(fileInfo?.tipo_archivo) && 
           !PREVIEWABLE_PDF.includes(fileInfo?.tipo_archivo) && <File className="w-5 h-5" />}
          <div>
            <h1 className="font-medium truncate max-w-md" title={fileInfo?.nombre}>
              {fileInfo?.nombre}
            </h1>
            <p className="text-xs text-gray-400">
              {formatFileSize(fileInfo?.tamano)} • {fileInfo?.tipo_adjunto || 'Documento'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            size="sm" 
            variant="ghost" 
            className="text-white hover:bg-gray-700"
            onClick={handleDownload}
            title="Descargar"
          >
            <Download className="w-4 h-4 mr-1" />
            Descargar
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            className="text-white hover:bg-gray-700"
            onClick={handleClose}
            title="Cerrar"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {renderPreview()}
      </div>
    </div>
  );
};

export default FilePreview;
